import {
  CellType,
  Cell,
  PlaceResult,
  EntityRef,
  SerializedGrid,
  Vec2,
} from "./types.ts";
import { GRID_WIDTH, GRID_HEIGHT } from "./config.ts";

export class Grid {
  readonly width: number;
  readonly height: number;
  private cells: Cell[][];
  private onCellChanged: (() => void) | null = null;

  constructor(width: number = GRID_WIDTH, height: number = GRID_HEIGHT) {
    this.width = width;
    this.height = height;
    this.cells = this.createEmptyGrid();
  }

  setOnCellChanged(callback: () => void): void {
    this.onCellChanged = callback;
  }

  private createEmptyGrid(): Cell[][] {
    const grid: Cell[][] = [];
    for (let y = 0; y < this.height; y++) {
      const row: Cell[] = [];
      for (let x = 0; x < this.width; x++) {
        row.push({
          type: CellType.EMPTY,
          x,
          y,
          blocksPath: false,
          blocksSynergy: false,
          occupant: null,
        });
      }
      grid.push(row);
    }
    return grid;
  }

  getCell(x: number, y: number): Cell | null {
    if (!this.isInBounds(x, y)) return null;
    return this.cells[y]![x]!;
  }

  isInBounds(x: number, y: number): boolean {
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  }

  canPlace(x: number, y: number, type: CellType): PlaceResult {
    if (!this.isInBounds(x, y)) {
      return { valid: false, reason: "OUT_OF_BOUNDS" };
    }

    const cell = this.cells[y]![x]!;

    if (
      cell.type === CellType.PATH_ENTRY ||
      cell.type === CellType.PATH_EXIT ||
      cell.type === CellType.DORMANT_SPAWN
    ) {
      return { valid: false, reason: "PROTECTED" };
    }

    if (
      cell.type !== CellType.EMPTY &&
      cell.type !== CellType.PATH_RESERVED &&
      cell.type !== CellType.HIDDEN_SPAWN
    ) {
      return { valid: false, reason: "OCCUPIED" };
    }

    // Path-blocking check delegated to Pathfinding system
    if (type === CellType.TOWER || type === CellType.WALL) {
      return { valid: true };
    }

    return { valid: true };
  }

  setCell(x: number, y: number, type: CellType, occupant?: EntityRef): boolean {
    if (!this.isInBounds(x, y)) return false;
    const cell = this.cells[y]![x]!;
    cell.type = type;
    cell.blocksPath =
      type === CellType.TOWER ||
      type === CellType.WALL ||
      type === CellType.OBSTACLE ||
      type === CellType.BLOCKER;
    cell.blocksSynergy = type === CellType.OBSTACLE;
    cell.occupant = occupant ?? null;
    this.onCellChanged?.();
    return true;
  }

  setCellWithHp(
    x: number,
    y: number,
    type: CellType,
    hp: number,
    occupant?: EntityRef,
  ): boolean {
    if (!this.setCell(x, y, type, occupant)) return false;
    this.cells[y]![x]!.hp = hp;
    return true;
  }

  damageCell(x: number, y: number, amount: number): boolean {
    if (!this.isInBounds(x, y)) return false;
    const cell = this.cells[y]![x]!;
    if (cell.hp === undefined) return false;
    cell.hp = Math.max(0, cell.hp - amount);
    if (cell.hp <= 0) {
      this.clearCell(x, y);
      return true; // destroyed
    }
    return false; // damaged but alive
  }

  getCellsInRadius(cx: number, cy: number, radius: number): Cell[] {
    const results: Cell[] = [];
    const r = Math.ceil(radius);
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx * dx + dy * dy <= radius * radius) {
          const cell = this.getCell(cx + dx, cy + dy);
          if (cell) results.push(cell);
        }
      }
    }
    return results;
  }

  getAdjacentCells(x: number, y: number): Cell[] {
    const results: Cell[] = [];
    const dirs = [
      [0, -1],
      [1, 0],
      [0, 1],
      [-1, 0],
    ];
    for (const [dx, dy] of dirs) {
      const cell = this.getCell(x + dx!, y + dy!);
      if (cell) results.push(cell);
    }
    return results;
  }

  clearCell(x: number, y: number): boolean {
    if (!this.isInBounds(x, y)) return false;
    const cell = this.cells[y]![x]!;
    cell.type = CellType.EMPTY;
    cell.blocksPath = false;
    cell.blocksSynergy = false;
    cell.occupant = null;
    delete cell.hp;
    this.onCellChanged?.();
    return true;
  }

  /** Lock the supplied path cells for the duration of the pre-wave countdown.
   *  Skips entries/exits/spawns which already have stricter rules. Returns
   *  the list of cells actually flipped so the caller can release them. */
  reservePath(cells: Vec2[]): Vec2[] {
    const reserved: Vec2[] = [];
    for (const { x, y } of cells) {
      const cell = this.getCell(x, y);
      if (!cell || cell.type !== CellType.EMPTY) continue;
      cell.type = CellType.PATH_RESERVED;
      reserved.push({ x, y });
    }
    this.onCellChanged?.();
    return reserved;
  }

  releaseReservedPath(cells: Vec2[]): void {
    for (const { x, y } of cells) {
      const cell = this.getCell(x, y);
      if (cell?.type === CellType.PATH_RESERVED) cell.type = CellType.EMPTY;
    }
    this.onCellChanged?.();
  }

  serialize(): SerializedGrid {
    const cells = [];
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const cell = this.cells[y]![x]!;
        if (cell.type !== CellType.EMPTY) {
          const serialized: {
            type: CellType;
            x: number;
            y: number;
            hp?: number;
            occupantId?: string;
          } = {
            type: cell.type,
            x: cell.x,
            y: cell.y,
          };
          if (cell.hp !== undefined) serialized.hp = cell.hp;
          if (cell.occupant?.id) serialized.occupantId = cell.occupant.id;
          cells.push(serialized);
        }
      }
    }
    return { width: this.width, height: this.height, cells };
  }

  static deserialize(data: SerializedGrid): Grid {
    const grid = new Grid(data.width, data.height);
    for (const cellData of data.cells) {
      const cell = grid.cells[cellData.y]![cellData.x]!;
      cell.type = cellData.type;
      cell.blocksPath =
        cellData.type === CellType.TOWER ||
        cellData.type === CellType.WALL ||
        cellData.type === CellType.OBSTACLE ||
        cellData.type === CellType.BLOCKER;
      cell.blocksSynergy = cellData.type === CellType.OBSTACLE;
      if (cellData.hp !== undefined) cell.hp = cellData.hp;
      if (cellData.occupantId)
        cell.occupant = {
          id: cellData.occupantId,
          kind: cellData.type === CellType.TOWER ? "tower" : "wall",
        };
    }
    return grid;
  }

  getPathBlockingCells(): Vec2[] {
    const result: Vec2[] = [];
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.cells[y]![x]!.blocksPath) {
          result.push({ x, y });
        }
      }
    }
    return result;
  }

  getEntries(): Vec2[] {
    return this.findCellsOfType(CellType.PATH_ENTRY);
  }

  getExits(): Vec2[] {
    return this.findCellsOfType(CellType.PATH_EXIT);
  }

  private findCellsOfType(type: CellType): Vec2[] {
    const result: Vec2[] = [];
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.cells[y]![x]!.type === type) {
          result.push({ x, y });
        }
      }
    }
    return result;
  }
}
